import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import omit from 'lodash/omit';
import FacilityEditForm from '../components/FacilityEditForm';
import useQuery from '../../modules/react/hooks/useQuery';
import useMutation from '../../modules/react/hooks/useMutation';
import { DEFAULT_LOCALE } from '../../modules/i18n/locales';

const GET_DATA = gql`
  query GetFacilityEditData($facilityId: ID!) {
    currentSite {
      id
      ...FacilityEditFormSite
    }
    listing(facilityId: $facilityId) {
      id
      ...FacilityEditFormListing
    }
    facility(id: $facilityId) {
      id
      ...FacilityEditFormFacility
    }
  }

  ${FacilityEditForm.fragments.listing}
  ${FacilityEditForm.fragments.site}
  ${FacilityEditForm.fragments.facility}
`;

// facilityLogoRemove(facilityId: $facilityId) @include(if: $removeLogo) {
//   facility {
//     id
//     theme {
//       logo {
//         path
//         mediumId
//         alt
//         title
//         link {
//           href
//           target
//         }
//       }
//     }
//   }
//   userErrors {
//     code
//     message
//   }
// }

// mutation ListingUpdate($facilityId: ID!, $listing: ListingInput!, $removeLogo: Boolean!) {

const UPDATE_LISTING = gql`
  mutation ListingUpdate($facilityId: ID!, $listing: ListingInput!) {
    listingUpdate(facilityId: $facilityId, listing: $listing) {
      listing {
        id
        ...FacilityEditFormListing
      }
      userErrors {
        code
        message
      }
    }
  }

  ${FacilityEditForm.fragments.listing}
`;

const UPDATE_MEDIUM = gql`
  mutation PrimaryImageMediumUpdate($id: ID!, $medium: MediumInput!) {
    mediumUpdate(id: $id, input: $medium) {
      medium {
        id
        metadata {
          id
          altText
          title
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`;

const propTypes = {
  facilityId: PropTypes.string.isRequired,
  setSubmitted: PropTypes.func,
  setSubmitting: PropTypes.func,
  setSubmitErrors: PropTypes.func,
};

const defaultProps = {
  setSubmitted: undefined,
  setSubmitting: undefined,
  setSubmitErrors: undefined,
};

const FacilityEdit = ({
  facilityId,
  setSubmitted,
  setSubmitting,
  setSubmitErrors,
  ...rest
}) => {
  const { data: facilityEditData } = useQuery(GET_DATA,
    {
      variables: { facilityId },
      fetchPolicy: 'network-only',
    });
  const [updateListing, { data, error, loading }] = useMutation(UPDATE_LISTING,
    { refetchQueries: [{ query: GET_DATA, variables: { facilityId } }] });
  const [updateMedium, { data: mediumData, error: mediumError }] = useMutation(UPDATE_MEDIUM);

  let submitErrors;
  let submitted;

  if (!facilityEditData) {
    return null;
  }

  const { listing, currentSite, facility } = facilityEditData;

  const onSubmit = (updatedListing) => {
    const { primaryImageMediumId, socialMediaPlatforms, socialMediaEnabled } = updatedListing;
    const formattedListing = omit(updatedListing, 'primaryImage', 'socialMediaPlatforms', 'socialMediaEnabled');
    formattedListing.primaryImageMediumId = primaryImageMediumId;
    formattedListing.socialMedia = {
      enabled: socialMediaEnabled,
      platforms: Object.entries(socialMediaPlatforms).map(
        ([platform, { enabled = false, link = '' }]) => ({ provider: platform, enabled, link }),
      ),
    };

    let payload = formattedListing;
    if (!formattedListing.theme.logo.path && !formattedListing.theme.logo.mediumId) {
      payload = omit(formattedListing, 'theme.logo');
    }

    updateListing(
      { variables: { facilityId, listing: payload } },
    );

    if (primaryImageMediumId) {
      const { alt, title } = updatedListing.primaryImage;
      const medium = {
        id: primaryImageMediumId,
        metadata: {
          altText: alt,
          locale: DEFAULT_LOCALE,
          title,
        },
      };
      updateMedium(
        { variables: { id: primaryImageMediumId, medium } },
      );
    }
    if (
      formattedListing.theme.logo.mediumId
      && formattedListing.theme.logo.mediumId !== primaryImageMediumId
    ) {
      const { alt, mediumId, title } = updatedListing.theme.logo;
      const medium = {
        id: mediumId,
        metadata: {
          altText: alt,
          locale: DEFAULT_LOCALE,
          title,
        },
      };
      updateMedium(
        { variables: { id: mediumId, medium } },
      );
    }
  };

  if (data) {
    const { listing: updatedListing, userErrors } = data.listingUpdate;

    if (updatedListing) {
      submitted = true;
      if (setSubmitted) {
        setSubmitted(true);
      }
    }

    if (userErrors.length > 0) {
      submitErrors = userErrors;
      submitted = false;
      if (setSubmitted && setSubmitErrors) {
        setSubmitted(false);
        setSubmitErrors(userErrors);
      }
    }
  }

  if (error) {
    const err = { code: 0, message: 'Could not update facility settings.' };

    submitErrors = submitErrors ? [err, ...submitErrors] : [err];
    submitted = false;
    if (setSubmitted && setSubmitErrors) {
      setSubmitted(false);
      setSubmitErrors(submitErrors);
    }
  }

  if (mediumData) {
    const { medium: updatedMedium, userErrors } = mediumData.mediumUpdate;

    if (updatedMedium) {
      submitted = true;
      if (setSubmitted) {
        setSubmitted(true);
      }
    }

    if (userErrors.length > 0) {
      submitErrors = userErrors;
      submitted = false;
      if (setSubmitted && setSubmitErrors) {
        setSubmitted(false);
        setSubmitErrors(userErrors);
      }
    }
  }

  if (mediumError) {
    const err = { code: 0, message: 'Could not update medium.' };

    submitErrors = submitErrors ? [err, ...submitErrors] : [err];
    submitted = false;
    if (setSubmitted && setSubmitErrors) {
      setSubmitted(false);
      setSubmitErrors(submitErrors);
    }
  }

  return (
    <FacilityEditForm
      {...rest}
      listing={listing}
      facility={facility}
      onSubmit={onSubmit}
      site={currentSite}
      submitted={submitted}
      submitting={loading}
      submitErrors={submitErrors}
    />
  );
};

FacilityEdit.propTypes = propTypes;
FacilityEdit.defaultProps = defaultProps;

export default FacilityEdit;
